import { useState, useEffect } from 'react';

/**
 * Custom hook to delay the setting of a loading state.
 * Useful for preventing brief flashes of loading indicators if loading finishes quickly.
 *
 * @param {boolean} isLoading The actual loading state.
 * @param {number} [delay=300] The delay in milliseconds before returning true.
 * @returns {boolean} The delayed loading state. Becomes true only if isLoading is true for longer than the delay.
 */
export const useDelayedLoadingState = (isLoading: boolean, delay = 300): boolean => {
  const [delayedLoadingState, setDelayedLoadingState] = useState<boolean>(false);

  useEffect(() => {
    let loadingTimeout: NodeJS.Timeout | null = null;

    if (isLoading) {
      loadingTimeout = setTimeout(() => {
        setDelayedLoadingState(true);
      }, delay);
    } else {
      if (loadingTimeout) {
        clearTimeout(loadingTimeout);
      }

      setDelayedLoadingState(false);
    }

    return () => {
      if (loadingTimeout) {
        clearTimeout(loadingTimeout);
      }
    };
  }, [isLoading, delay]);

  return delayedLoadingState;
};
